// Demo administration (PRD §3.5): reset to the fixtures, load an acceptance scenario.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../components/Layout.tsx';
import { ErrorBox } from '../components/ui.tsx';
import { myRoles } from '../lib/data.ts';
import { invoke } from '../lib/supabase.ts';

interface Scenario {
  name: string;
  test: string;
  title: string;
  batch_id: string;
  mode: string;
  assumptions: string[];
}

export function Admin() {
  const { t } = useTranslation();
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [admin, setAdmin] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        setAdmin((await myRoles()).includes('platform_admin'));
        setScenarios((await invoke<{ scenarios: Scenario[] }>('admin-reset', { action: 'list' })).scenarios);
      } catch (e) {
        setError(e);
      }
    })();
  }, []);

  async function act(body: Record<string, unknown>, done: string) {
    setBusy(true);
    setError(null);
    try {
      await invoke('admin-reset', body);
      setMsg(done);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main>
      <PageHeader title={t('admin.title')} page="admin" />
      <ErrorBox error={error} />
      {!admin && <p className="muted">{t('admin.only_admin')}</p>}
      {msg && <p className="ok">{msg}</p>}
      <button disabled={!admin || busy} onClick={() => void act({ action: 'reset' }, t('admin.reset_done'))}>
        {t('admin.reset')}
      </button>
      <p className="hint">{t('admin.reset_help')}</p>
      <h2>{t('admin.scenarios')}</h2>
      <p className="hint">{t('admin.scenarios_help')}</p>
      <table>
        <tbody>
          {scenarios.map((s) => (
            <tr key={s.name}>
              <td>
                <strong>{s.test}</strong> {s.title}
              </td>
              <td>{s.batch_id}</td>
              <td>
                {s.mode} {s.assumptions.join(', ')}
              </td>
              <td>
                <button
                  disabled={!admin || busy}
                  onClick={() =>
                    void act({ action: 'load_scenario', name: s.name }, `${t('admin.loaded')} ${s.test}`)
                  }
                >
                  {t('admin.load')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
